import { useState } from "react";
import {
  Sparkles, ExternalLink, Copy, ChevronDown, ChevronUp,
  AlertCircle, RefreshCw, ArrowRight, Search, Shield,
  FileText, Globe, AlertTriangle, Database, Folder, Cloud, Cpu,
  LogIn,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useDorkSuggestions, Suggestion } from "@/hooks/useDorkSuggestions";
import { toast } from "@/hooks/use-toast";
import { useLocale } from "@/contexts/LocaleContext";

interface SuggestedDorksProps {
  keyword: string;
  onSelectDork: (query: string) => void;
}

const categoryConfig: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string; label: string }> = {
  osint: { icon: Search, color: "text-cyan-400", label: "OSINT" },
  security: { icon: Shield, color: "text-green-400", label: "Security" },
  academic: { icon: FileText, color: "text-purple-400", label: "Academic" },
  "file-discovery": { icon: FileText, color: "text-orange-400", label: "File Discovery" },
  research: { icon: Globe, color: "text-blue-400", label: "Research" },
  vulnerabilities: { icon: AlertTriangle, color: "text-red-400", label: "Vulnerabilities" },
  databases: { icon: Database, color: "text-yellow-400", label: "Databases" },
  "sensitive-directories": { icon: Folder, color: "text-amber-400", label: "Sensitive Dirs" },
  cloud: { icon: Cloud, color: "text-sky-400", label: "Cloud" },
  iot: { icon: Cpu, color: "text-teal-400", label: "IoT" },
  custom: { icon: Globe, color: "text-muted-foreground", label: "Custom" },
  "sensitive-files": { icon: FileText, color: "text-orange-400", label: "Sensitive Files" },
};

const riskConfig: Record<string, { color: string; bg: string }> = {
  low: { color: "text-green-400", bg: "bg-green-400/10 border-green-400/20" },
  medium: { color: "text-yellow-400", bg: "bg-yellow-400/10 border-yellow-400/20" },
  high: { color: "text-orange-400", bg: "bg-orange-400/10 border-orange-400/20" },
};

function SkeletonCard() {
  return (
    <div className="glass-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
      <Skeleton className="h-10 w-full rounded-lg" />
      <Skeleton className="h-4 w-3/4" />
      <div className="flex gap-2">
        <Skeleton className="h-8 flex-1 rounded-lg" />
        <Skeleton className="h-8 flex-1 rounded-lg" />
        <Skeleton className="h-8 w-24 rounded-lg" />
      </div>
    </div>
  );
}

function SuggestionCard({
  suggestion,
  onSelectDork,
}: {
  suggestion: Suggestion;
  onSelectDork: (q: string) => void;
}) {
  const { t } = useLocale();
  const [expanded, setExpanded] = useState(false);
  const cat = categoryConfig[suggestion.category] || categoryConfig.custom;
  const risk = riskConfig[suggestion.risk] || riskConfig.low;
  const CategoryIcon = cat.icon;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(suggestion.query);
    toast({ title: t("suggest.copied_title"), description: t("suggest.copied_desc") });
  };

  const handleOpenSearch = () => {
    const url = `https://www.google.com/search?q=${encodeURIComponent(suggestion.query)}`;
    if (typeof window !== "undefined" && (window as any).Telegram?.WebApp?.openLink) {
      (window as any).Telegram.WebApp.openLink(url);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="glass-card p-4 space-y-3 animate-fade-in">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CategoryIcon className={`w-4 h-4 ${cat.color}`} />
          <span className={`text-xs font-semibold uppercase tracking-wider ${cat.color}`}>
            {cat.label}
          </span>
        </div>
        <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${risk.bg} ${risk.color}`}>
          {suggestion.risk}
        </span>
      </div>

      <div className="bg-muted/30 rounded-lg px-3 py-2 border border-border/30">
        <code className="text-sm font-mono text-foreground break-all leading-relaxed">
          {suggestion.query}
        </code>
      </div>

      {suggestion.description && (
        <p className="text-xs text-muted-foreground leading-relaxed">{suggestion.description}</p>
      )}

      {suggestion.examples && suggestion.examples.length > 0 && (
        <div>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? t("suggest.hide") : t("suggest.show")} {t("suggest.examples")} ({suggestion.examples.length})
          </button>
          {expanded && (
            <ul className="mt-2 space-y-1 pl-3 border-l border-border/40">
              {suggestion.examples.map((ex, i) => (
                <li key={i} className="text-xs text-muted-foreground/70">{ex}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button
          onClick={handleCopy}
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-muted/40 hover:bg-muted/70 text-foreground text-xs font-medium transition-all border border-border/30 hover:border-primary/30"
          aria-label="Copy query"
        >
          <Copy className="w-3.5 h-3.5" />
          {t("suggest.copy")}
        </button>
        <button
          onClick={handleOpenSearch}
          disabled={!suggestion.query.trim()}
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-muted/40 hover:bg-muted/70 text-foreground text-xs font-medium transition-all border border-border/30 hover:border-primary/30 disabled:opacity-50"
          aria-label="Open Google search"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          {t("suggest.search")}
        </button>
        <button
          onClick={() => onSelectDork(suggestion.query)}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-primary/20 to-secondary/20 hover:from-primary/30 hover:to-secondary/30 text-foreground text-xs font-medium transition-all border border-primary/30 hover:border-primary/50"
        >
          <ArrowRight className="w-3.5 h-3.5" />
          {t("suggest.use")}
        </button>
      </div>
    </div>
  );
}

export const SuggestedDorks = ({ keyword, onSelectDork }: SuggestedDorksProps) => {
  const { t } = useLocale();
  const { suggestions, isLoading, error, needsAuth, retry } = useDorkSuggestions(keyword);

  if (keyword.length < 2) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm px-1">
        <Sparkles className="w-4 h-4 text-primary" />
        <span className="font-medium text-foreground">{t("suggest.title", { keyword })}</span>
        {isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse ml-auto">{t("suggest.generating")}</span>
        )}
      </div>

      {needsAuth && !isLoading && (
        <div className="glass-card p-4 flex items-center gap-3 border-primary/20">
          <LogIn className="w-5 h-5 text-primary shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">{t("suggest.sign_in_title")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t("suggest.sign_in_desc")}</p>
          </div>
          <a
            href="/auth"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary/20 hover:bg-primary/30 text-primary text-xs font-medium transition-all border border-primary/30 hover:border-primary/50 shrink-0"
          >
            <LogIn className="w-3.5 h-3.5" />
            {t("suggest.sign_in_btn")}
          </a>
        </div>
      )}

      {error && !isLoading && !needsAuth && (
        <div className="glass-card p-3 flex items-center gap-3 border-destructive/30">
          <AlertCircle className="w-4 h-4 text-destructive shrink-0" />
          <span className="text-xs text-muted-foreground flex-1">{error}</span>
          <button
            onClick={retry}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors shrink-0"
          >
            <RefreshCw className="w-3 h-3" />
            {t("suggest.retry")}
          </button>
        </div>
      )}

      {isLoading && (
        <div className="space-y-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {!isLoading && !error && !needsAuth && suggestions.length > 0 && (
        <div className="space-y-3">
          {suggestions.map((s, i) => (
            <SuggestionCard key={`${s.query}-${i}`} suggestion={s} onSelectDork={onSelectDork} />
          ))}
        </div>
      )}

      {!isLoading && !error && !needsAuth && suggestions.length === 0 && (
        <div className="glass-card p-4 text-center text-xs text-muted-foreground">
          {t("suggest.no_results")}
        </div>
      )}
    </div>
  );
};
